import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
  const { getAuthenticatedApi, user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [stats, setStats] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');

  const USERS_PER_PAGE = 20;

  // Match backend VALID_ROLES exactly
  const VALID_ROLES = [
    { value: 'ADMIN', label: 'Admin', color: 'text-red-400 bg-red-900/20' },
    { value: 'MODERATOR', label: 'Moderator', color: 'text-blue-400 bg-blue-900/20' },
    { value: 'USER', label: 'User', color: 'text-gray-400 bg-gray-800/20' },
    { value: 'ANALYST', label: 'Analyst', color: 'text-yellow-400 bg-yellow-900/20' },
    { value: 'BLOG_WRITER', label: 'Blog Writer', color: 'text-green-400 bg-green-900/20' },
  ];

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      if (error.detail) {
        if (typeof error.detail === 'string') {
          return error.detail;
        }
        if (Array.isArray(error.detail)) {
          return error.detail.map(err =>
            typeof err === 'string' ? err : err.msg || JSON.stringify(err)
          ).join(', ');
        }
        return JSON.stringify(error.detail);
      }

      if (error.message) {
        return error.message;
      }

      if (error.msg) {
        return error.msg;
      }

      return JSON.stringify(error);
    }

    return 'An unknown error occurred';
  };

  useEffect(() => {
    if (user?.role?.toLowerCase() === 'admin') {
      fetchUsers();
    }
  }, [user, currentPage, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      const skip = (currentPage - 1) * USERS_PER_PAGE;
      let url = `/admin/users?skip=${skip}&limit=${USERS_PER_PAGE}`;
      if (roleFilter) {
        url += `&role_filter=${roleFilter}`;
      }
      const response = await api.get(url);
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total_count || 0);
      setError(null);
    } catch (err) {
      console.error('Fetch users error:', err);
      const errorMessage = getErrorMessage(err.response?.data || err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchQuery('');
      setCurrentPage(1);
      fetchUsers();
      return;
    }

    if (searchQuery.trim().length < 2) {
      setError('Search query must be at least 2 characters long');
      return;
    }

    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      const response = await api.get(`/admin/search?query=${encodeURIComponent(searchQuery)}&limit=50`);
      setUsers(response.data.users || []);
      setTotalUsers(response.data.result_count || 0);
      setError(null);
    } catch (err) {
      console.error('Search error:', err);
      const errorMessage = getErrorMessage(err.response?.data || err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ✅ REPLACE THE TRY BLOCK:
  const handleRoleChange = async () => {
    if (!selectedUser || !newRole) {
      setError('Please select a role');
      return;
    }

    if (selectedUser.id === user.id && newRole !== 'ADMIN') {
      setError("You cannot demote your own admin role.");
      return;
    }

    try {
      const api = getAuthenticatedApi();
      await api.patch(`/admin/${selectedUser.id}/role`, {
        new_role: newRole.toUpperCase(),
      });

      await fetchUsers();
      setShowRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      setError(null);
    } catch (err) {
      console.error('Role change error:', err);
      const errorMessage = getErrorMessage(err.response?.data || err);
      setError(errorMessage);
    }
  };

  // ✅ REPLACE THE TRY BLOCK:
  const toggleUserStatus = async (userId, currentStatus) => {
    if (userId === user.id && currentStatus) {
      setError("You cannot deactivate your own account.");
      return;
    }

    try {
      const api = getAuthenticatedApi();
      await api.patch(`/admin/${userId}/status`, { is_active: !currentStatus });
      await fetchUsers();
      setError(null);
    } catch (err) {
      console.error('Status toggle error:', err);
      const errorMessage = getErrorMessage(err.response?.data || err);
      setError(errorMessage);
    }
  };


  // ✅ REPLACE THE TRY BLOCK:
  const deleteUser = async (userId) => {
    if (userId === user.id) {
      setError("You cannot delete your own account.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }

    try {
      const api = getAuthenticatedApi();
      await api.delete(`/admin/${userId}`);
      await fetchUsers();
      setError(null);
    } catch (err) {
      console.error('Delete user error:', err);
      const errorMessage = getErrorMessage(err.response?.data || err);
      setError(errorMessage);
    }
  };


  const getRoleInfo = (role) => {
    const roleStr = String(role || '').toUpperCase();
    const roleInfo = VALID_ROLES.find(r => r.value === roleStr);
    return roleInfo || { value: roleStr, label: roleStr, color: 'text-gray-400 bg-gray-800/20' };
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    fetchUsers();
  };

  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);

  // Check if user has admin access
  if (!user || user.role?.toLowerCase() !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-xl md:text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-6">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/forum')}
            className="px-6 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-500/30 text-blue-400 rounded-lg hover:bg-blue-600/30 hover:border-blue-400/50 transition-all duration-300"
          >
            Back to Forum
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/forum')}
              className="group px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 hover:border-white/20 hover:text-white transition-all duration-300 flex items-center gap-2"
            >
              <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Forum
            </button>
            <h1 className="text-2xl md:text-3xl font-bold">Admin Panel</h1>
          </div>

          {stats && (
            <div className="text-xs md:text-sm text-gray-400 flex flex-wrap gap-4">
              <span>Total: {stats.total_users || 0}</span>
              <span>Active: {stats.active_users || 0}</span>
              <span>Inactive: {stats.inactive_users || 0}</span>
            </div>
          )}
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
              placeholder="Search users by name or email (min 2 chars)..."
              className="w-full bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 px-4 py-2 rounded-lg text-white pr-10 focus:border-blue-500/50 focus:outline-none transition-colors"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                ×
              </button>
            )}
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 px-4 py-2 rounded-lg text-white min-w-[120px] focus:border-blue-500/50 focus:outline-none"
          >
            <option value="">All Roles</option>
            {VALID_ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>

          <button
            onClick={searchUsers}
            disabled={loading}
            className="bg-blue-600/80 backdrop-blur-sm hover:bg-blue-600 disabled:bg-blue-800/50 disabled:cursor-not-allowed px-6 py-2 rounded-lg text-white transition-all duration-300 whitespace-nowrap"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-400 bg-red-900/20 backdrop-blur-sm border border-red-800/50 px-4 py-3 rounded-lg mb-4 flex justify-between items-center"
          >
            <span className="text-sm">{String(error)}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 ml-4 text-xl leading-none"
            >
              ×
            </button>
          </motion.div>
        )}

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            <span className="ml-3 text-gray-400">Loading users...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>No users found</p>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="mt-2 text-blue-400 hover:underline"
              >
                Clear search and show all users
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block bg-gray-900/50 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-800/50">
              <table className="w-full text-sm">
                <thead className="bg-gray-800/50 text-gray-300 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">ID</th>
                    <th className="px-4 py-3 text-left font-medium">Name</th>
                    <th className="px-4 py-3 text-left font-medium">Email</th>
                    <th className="px-4 py-3 text-left font-medium">Role</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Last Active</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, index) => {
                    const roleInfo = getRoleInfo(u.role);
                    return (
                      <motion.tr
                        key={u.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-t border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-400">#{u.id}</td>
                        <td className="px-4 py-3 font-medium">{String(u.name || '')}</td>
                        <td className="px-4 py-3 text-gray-300">{String(u.email || '')}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${u.is_active
                            ? 'text-green-400 bg-green-900/20'
                            : 'text-red-400 bg-red-900/20'
                            }`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {u.last_active
                            ? new Date(u.last_active).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setNewRole(String(u.role || 'USER').toUpperCase());
                                setShowRoleModal(true);
                              }}
                              className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded hover:bg-blue-900/20 transition-colors"
                            >
                              Edit Role
                            </button>
                            <button
                              onClick={() => toggleUserStatus(u.id, u.is_active)}
                              className={`text-xs px-2 py-1 rounded transition-colors ${u.is_active
                                ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20'
                                : 'text-green-400 hover:text-green-300 hover:bg-green-900/20'
                                }`}
                            >
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => deleteUser(u.id)}
                              className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-red-900/20 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4">
              {users.map((u, index) => {
                const roleInfo = getRoleInfo(u.role);
                return (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-gray-900/50 backdrop-blur-sm border border-gray-800/50 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium text-white">{String(u.name || '')}</h3>
                        <p className="text-sm text-gray-400">#{u.id} • {String(u.email || '')}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${u.is_active
                        ? 'text-green-400 bg-green-900/20'
                        : 'text-red-400 bg-red-900/20'
                        }`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-xs text-gray-400">
                        Last active: {u.last_active
                          ? new Date(u.last_active).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => {
                          setSelectedUser(u);
                          setNewRole(String(u.role || 'USER').toUpperCase());
                          setShowRoleModal(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 text-xs px-3 py-1 rounded bg-blue-900/20 hover:bg-blue-900/30 transition-colors"
                      >
                        Edit Role
                      </button>
                      <button
                        onClick={() => toggleUserStatus(u.id, u.is_active)}
                        className={`text-xs px-3 py-1 rounded transition-colors ${u.is_active
                          ? 'text-yellow-400 bg-yellow-900/20 hover:bg-yellow-900/30'
                          : 'text-green-400 bg-green-900/20 hover:bg-green-900/30'
                          }`}
                      >
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => deleteUser(u.id)}
                        className="text-red-400 hover:text-red-300 text-xs px-3 py-1 rounded bg-red-900/20 hover:bg-red-900/30 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-400 text-center sm:text-left">
                  Showing {((currentPage - 1) * USERS_PER_PAGE) + 1} to {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    className="px-3 py-2 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700/50 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-400">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    className="px-3 py-2 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700/50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Role Change Modal */}
        <AnimatePresence>
          {showRoleModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setShowRoleModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-gray-900/95 backdrop-blur-sm p-6 rounded-lg shadow-xl w-full max-w-md border border-gray-700/50"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-xl font-semibold mb-4">
                  Change Role for {String(selectedUser.name || '')}
                </h2>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    New Role
                  </label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="w-full p-3 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg text-white focus:border-blue-500/50 focus:outline-none"
                  >
                    {VALID_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowRoleModal(false)}
                    className="px-4 py-2 bg-gray-700/50 backdrop-blur-sm hover:bg-gray-600/50 rounded-lg text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRoleChange}
                    disabled={!newRole}
                    className="px-4 py-2 bg-blue-600/80 backdrop-blur-sm hover:bg-blue-600 disabled:bg-blue-800/50 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
                  >
                    Update Role
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminPanel;